import {Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnInit, Output, Renderer2, SimpleChanges, ViewChild, ViewEncapsulation} from '@angular/core';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {OrderLinesGridService} from '@application/helper/orderbook/order-lines-grid.service';
import {DraggingService} from '@application/services/dragging/dragging.service';
import {FixedShape} from '@domain/drawing-library/fixed-shape.enum';
import {OrderLinesTarget} from '@domain/order-lines-target.enum';
import {ProductionOrderCustomSettings} from '@domain/production-schedule/custom-settings/production-order-custom-settings';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionScheduleDrawingViewMode} from '@domain/production-schedule/production-schedule-drawing-view-mode.enum';
import {ProductionScheduleItemInPathGroup} from '@domain/production-schedule/production-schedule-item-in-path-group';
import {ProductionScheduleOrderLine} from '@domain/production-schedule/production-schedule-order-line';
import {ProductionSchedulePath} from '@domain/production-schedule/production-schedule-path';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription} from '@domain/profile/subscription';
import {ColorSet} from '@domain/textile-data/color-set/color-set';
import {ColoredYarnSet} from '@domain/textile-data/colored-yarn-set/colored-yarn-set';
import {OverviewListColoredYarnSetWithStartDent} from '@domain/textile-data/creel/overview-list-colored-yarn-set-with-start-dent';
import {CarpetWeaveQuality} from '@domain/textile-data/machine-quality/carpet-weave-quality';
import {PathWidth} from '@domain/textile-data/machine-quality/path-width';
import {WeaveQuality} from '@domain/textile-data/machine-quality/weave-quality';
import {StatusBarTarget} from '@domain/textile-data/status-bar-target.constants';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {COLOR_SETS, ColorSets} from '@infrastructure/http/color-set/color-sets';
import {COLORED_YARN_SETS, ColoredYarnSets} from '@infrastructure/http/colored-yarn-set/colored-yarn-sets';
import {CREELS, Creels} from '@infrastructure/http/creel/creels';
import {MACHINE_QUALITIES, MachineQualities} from '@infrastructure/http/machine-quality/machine-qualities';
import {ORDERBOOK, Orderbook} from '@infrastructure/http/orderbook/orderbook';
import {HttpStatusBarService} from '@infrastructure/http/status-bar/http-status-bar.service';
import {OrderlineSettingsData} from '@presentation/components/dialog-button/dialog-data/order-lines-settings/order-lines-setting-data.interface';
import {OrderLinesCustomSettingsDialogComponent} from '@presentation/components/dialog-button/dialog-data/order-lines-settings/order-lines-settings.component';
import {ListDrawingOrderLine} from '@presentation/components/drawing-list/list-drawing-order-line';
import {SearchFiltersComponent} from '@presentation/components/search-filters/search-filters.component';
import {OverviewListOrderLine} from '@presentation/pages/texfab/orderbook/overview/overview-list-order-line';
import {PRODUCTION_SCHEDULE_PLAN, ProductionSchedulePlan} from '@presentation/pages/texfab/production-schedule/add/plan/production-schedule-plan';
import {OverviewListCreel} from '@presentation/pages/textile-data/creel/overview/overview-list-creel';
import {
  AgGridUtils,
  AssertionUtils,
  BaseComponent,
  ColDefBuilderFactoryService,
  DialogBuilderFactoryService,
  DialogType,
  FilterComponent,
  GridModel,
  GridOptionsBuilder,
  GridOptionsBuilderFactoryService,
  GridTagComponent,
  LoadingCellOverlayComponent,
  LocalStorageService,
  NumberCellEditorComponent,
  NumberCellEditorParams,
  OverlayActionsService,
  OverlayComponentParamsAction,
  Priority,
  ProgressCellRendererComponent,
  StatusBar,
  StringUtils,
  TagColor,
  TagSize,
  TranslateService,
  Unit
} from '@vdw/angular-component-library';
import {AgGridAngular} from 'ag-grid-angular';
import {
  CellClickedEvent,
  CellEditingStoppedEvent,
  CellRendererSelectorResult,
  CellValueChangedEvent,
  ColDef,
  Column,
  DndSourceCallbackParams,
  EditableCallbackParams,
  FilterModel,
  GetRowIdParams,
  GridApi,
  GridOptions,
  ICellEditorParams,
  ICellRendererParams,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  ITooltipParams,
  RowDoubleClickedEvent,
  RowNode,
  RowSelectedEvent,
  SortModelItem,
  ValueGetterParams,
  ValueSetterParams
} from 'ag-grid-community';
import {L10nIntlService} from 'angular-l10n';
import {forkJoin, Observable, of, Subject} from 'rxjs';
import {map, mergeMap, startWith, switchMap, takeUntil} from 'rxjs/operators';
import {AddProductionScheduleNavigationState} from '../../../add-production-schedule-navigation-state.interface';
import {OrderLinesHelper} from '../order-lines-helper';
import {OrderLineStatusCellRendererParams} from './order-line-status-cell-renderer/order-line-status-cell-renderer-params.interface';
import {OrderLineStatusCellRendererComponent} from './order-line-status-cell-renderer/order-line-status-cell-renderer.component';
import {ProductionScheduleItemInPathGroupStatusCellRendererComponent} from './production-schedule-item-in-path-group-status-cell-renderer/production-schedule-item-in-path-group-status-cell-renderer.component';
import {ProductionScheduleItemInPathGroupWithIdentification} from './production-schedule-item-in-path-group-with-identification';
import {ProductionScheduleOrderLineWithQuantity} from './production-schedule-order-line-with-quantity';

type RowData = ProductionScheduleOrderLineWithQuantity | ProductionScheduleItemInPathGroupWithIdentification;

@Component({
  selector: 'app-drawing-library-order-lines-view',
  templateUrl: './order-lines-view.component.html',
  styleUrls: ['./order-lines-view.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class OrderLinesViewComponent extends BaseComponent implements OnInit, OnChanges {
  public static DEFAULT_PADDING_IN_PX = 16;
  public static DELETE_PATH_LABEL_BUTTON_WIDTH_IN_PX = 44;
  public static MIN_WIDTH_FILTER_IN_PX = 244;
  public static WIDTH_TEXT_FILTER_IN_PX = 184;

  @Input() public displayTechnicalUnit = true;
  @Input() public productionSchedule: ProductionSchedule;
  @Input() public productionOrderCustomSettings: ProductionOrderCustomSettings;
  @Input() public commercialUnit: Unit;
  @Input() public isPreSelection = false;
  @Input() public selectedPath: ProductionSchedulePath;
  @Input() public selectedRestPathColoredYarnSet: OverviewListColoredYarnSetWithStartDent;
  @Input() public productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet;
  @Input() public pathWidths: PathWidth[] = [];
  @Input() public availableSpaceInBuggyInPicks: number;
  @Input() public buggyLengthMaxInPicks: number;
  @Input() public marginInDentsOfMissingDesigns: number;
  @Input() public preselectedOrderlineIds: number[];
  @Input() public addProductionScheduleNavigationState: AddProductionScheduleNavigationState = null;
  @Input() public productionScheduleItemMultiplier: number;
  @Input() public selectedPathContentRowData: ProductionScheduleItemInPathGroup;
  @Output() public orderlineSelected = new EventEmitter<number[]>();
  @Output() public selectedViewModeChanged = new EventEmitter<ProductionScheduleDrawingViewMode>();
  @Output() public navigateBackOutput = new EventEmitter<any>();
  @Output() public selectedPathOutput: EventEmitter<ProductionSchedulePath> = new EventEmitter<ProductionSchedulePath>();
  @Output() public selectedRestPathColoredYarnSetOutput: EventEmitter<OverviewListColoredYarnSetWithStartDent> = new EventEmitter<OverviewListColoredYarnSetWithStartDent>();

  @ViewChild('orderLinesNewViewGrid')
  public orderLinesNewViewGrid: AgGridAngular;

  @ViewChild('container')
  public containerElement: ElementRef;

  @ViewChild('fixedButtons')
  public fixedButtonsElement: ElementRef;

  @ViewChild('pathButtons')
  public pathButtonsElement: ElementRef;

  @ViewChild('searchFilters')
  public searchFilters: SearchFiltersComponent;

  @ViewChild('filter')
  public filter: FilterComponent;

  public overflowPathsOpen = false;
  public widthForFilters: string;
  public statusBar: StatusBar;
  public gridOptions: GridOptions;
  public loadingForTheFirstTime = true;
  public gridModel: GridModel;
  public readonly customSettingsComponent = OrderLinesCustomSettingsDialogComponent;
  public viewModes: ProductionScheduleDrawingViewMode[] = [
    ProductionScheduleDrawingViewMode.DESIGNS_LIST,
    ProductionScheduleDrawingViewMode.ORDER_LINES,
    ProductionScheduleDrawingViewMode.GROUPED_ORDER_LINES,
    ProductionScheduleDrawingViewMode.GROUPED_DESIGNS
  ];

  public selectedViewMode = ProductionScheduleDrawingViewMode.ORDER_LINES;
  public onlyShowSelected = false;

  private orderlinesThatAreUsedToAutoFill: ProductionScheduleOrderLineWithQuantity[];
  private availableSpaceForAutoFill: number;
  private rollLengthColumnId = 'rollLength';
  private readonly SLICE_SIZE = 30;
  private showOnlyQueryCompatibleOrderlines = true;
  private showPreselectedItems = false;
  private orderLines: ProductionScheduleOrderLineWithQuantity[];
  private alternativeQualityNames: string[] = [];
  private alternativeColorSetNames: string[] = [];
  private qualityNames: string[] = [];
  private fullQualities: CarpetWeaveQuality[] = [];
  private colorSetNames: string[] = [];
  private creelColorSetIds: number[] = [];
  private readyToFetchRows: boolean = false;
  private readonly quantityTranslation = 'ORDERBOOK.QUANTITY';
  private readonly booleanFalseTranslation = 'GENERAL.CONDITION.NO';
  private readonly booleanTrueTranslation = 'GENERAL.CONDITION.YES';
  private readonly syncWithProductionScheduleSubject = new Subject<void>();
  private readonly noOverlayAction: OverlayComponentParamsAction[] = [
    {
      key: 'addEventRule',
      titleKey: this.translate.instant('PRODUCTION_ORDER.WEAVE_PLAN.PRESELECTION.PRESELECTION'),
      isPrimary: true,
      disabled: false
    }
  ];

  private preFilterDataFetched: boolean = false;
  private processRowSelection: boolean = true;
  private hasQuantitySuggestionPermission: boolean = false;

  public constructor(
    @Inject(AUTHENTICATION) private authentication: Authentication,
    @Inject(ORDERBOOK) private readonly orderbook: Orderbook,
    @Inject(PRODUCTION_SCHEDULE_PLAN) private readonly productionSchedulePlan: ProductionSchedulePlan,
    @Inject(MACHINE_QUALITIES) private readonly qualities: MachineQualities,
    @Inject(COLORED_YARN_SETS) private readonly coloredYarnSets: ColoredYarnSets,
    @Inject(COLOR_SETS) private readonly colorSets: ColorSets,
    @Inject(CREELS) private readonly creelsService: Creels,
    private readonly gridOptionsBuilderFactoryService: GridOptionsBuilderFactoryService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly translate: TranslateService,
    private readonly draggingService: DraggingService,
    private readonly renderer: Renderer2,
    private readonly l10nIntlService: L10nIntlService,
    private readonly orderLinesGridService: OrderLinesGridService,
    private readonly statusBarService: HttpStatusBarService,
    private readonly overlayActionsService: OverlayActionsService,
    private readonly orderLinesHelper: OrderLinesHelper,
    private readonly localStorage: LocalStorageService
  ) {
    super();
    this.orderLinesHelper.commercialUnit = this.commercialUnit;
  }

  public ngOnInit(): void {
    const currentSubscription = this.authentication.getCurrentSubscription();

    this.filterViewModes(currentSubscription);

    this.initialiseGridOptions();
    this.subscribeToProductionScheduleChanges();
    this.subscribeToOverlayActions();

    if (!AssertionUtils.isEmpty(this.preselectedOrderlineIds) && !this.isPreSelection) {
      this.showPreselectedItems = true;
    }

    if (!this.isPreSelection) {
      this.viewModes.push(ProductionScheduleDrawingViewMode.GROUPED_DESIGN_THUMBNAILS);
    }

    this.selectedViewMode = this.localStorage.get('drawingLibraryViewMode') || this.viewModes[0];
    this.hasQuantitySuggestionPermission = currentSubscription.hasPermission(Permission.TEXFAB_QUANTITY_SUGGESTION);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.isPreSelection) {
      this.setInitializationFilters();
    }

    if (!AssertionUtils.isEmpty(this.preselectedOrderlineIds) && !this.isPreSelection) {
      this.showPreselectedItems = true;
      this.orderLinesNewViewGrid?.api.redrawRows();
    }

    if ('productionSchedule' in changes || ('selectedPath' in changes && !AssertionUtils.isNullOrUndefined(this.orderLinesNewViewGrid?.api))) {
      this.orderLinesNewViewGrid?.api?.forEachNode((rowNode: RowNode<ProductionScheduleOrderLineWithQuantity>) => {
        if (!AssertionUtils.isNullOrUndefined(rowNode.data)) {
          const value = this.orderLinesHelper.getQuantitySuggestionForPath(
            rowNode.data,
            this.productionSchedule,
            this.selectedPath,
            this.availableSpaceInBuggyInPicks,
            this.buggyLengthMaxInPicks,
            this.fullQualities,
            this.hasQuantitySuggestionPermission,
            this.productionScheduleItemMultiplier
          );
          rowNode.setDataValue('quantity', value);
        }
      });
    }
  }

  public trackByLabeledPath = (_: number, labeledPath: {label: number; path: ProductionSchedulePath}): number => labeledPath.label;

  public onSelectedViewModeChanged(selectedViewMode: ProductionScheduleDrawingViewMode): void {
    this.localStorage.set('drawingLibraryViewMode', selectedViewMode);
    this.selectedViewModeChanged.emit(selectedViewMode);
  }

  public onCustomSettingsChanged(settings: {showPreselectedItems: boolean; showOnlyQueryCompatibleOrderlines: boolean}): void {
    this.showPreselectedItems = settings.showPreselectedItems ?? this.showPreselectedItems;
    this.showOnlyQueryCompatibleOrderlines = settings.showOnlyQueryCompatibleOrderlines ?? this.showOnlyQueryCompatibleOrderlines;
    this.setInitializationFilters();
  }

  public getCustomSettingsDialogData(): any {
    return {
      showPreselectedItems: this.showPreselectedItems,
      showOnlyQueryCompatibleOrderlines: this.showOnlyQueryCompatibleOrderlines,
      viewMode: this.selectedViewMode
    } as OrderlineSettingsData;
  }

  public getSelectedRows(): number {
    return this.preselectedOrderlineIds ? this.preselectedOrderlineIds.length : 0;
  }

  public toggleSelectedRows(change: boolean): void {
    if (change) {
      this.onlyShowSelected = true;
      this.orderLinesHelper.savedFilterModel = null;
      this.orderLinesNewViewGrid.api.setFilterModel(null);

      if (!AssertionUtils.isEmpty(this.preselectedOrderlineIds)) {
        const orderlineIds = this.preselectedOrderlineIds.map((id: number) => `${id}`);

        const filterModel = {
          id: {
            filterModels: [null, {values: orderlineIds, filterType: 'set'}],
            filterType: 'multi'
          }
        };

        this.orderLinesHelper.savedFilterModel = filterModel;
        this.orderLinesNewViewGrid.api.setFilterModel(filterModel);
      }
    } else {
      this.onlyShowSelected = false;
      this.setInitializationFilters();
    }
  }

  public openOverflowPaths(): void {
    this.overflowPathsOpen = true;
  }

  public closeOverflowPaths(): void {
    this.overflowPathsOpen = false;
  }

  public isSelectingOverlappingOrderLinesAndProductionScheduleItems(): boolean {
    const selectedOrderlines = this.getSelectedOrderLines().map((productionScheduleOrderLineWithQuantity: ProductionScheduleOrderLineWithQuantity) => productionScheduleOrderLineWithQuantity.id);

    const productionScheduleItemsFromSelectedOrderLines = this.orderLinesHelper
      .getProductionScheduleItemsInPathGroup(this.productionSchedule)
      .filter((productionScheduleItemInPathGroup: ProductionScheduleItemInPathGroup) => selectedOrderlines.includes(productionScheduleItemInPathGroup.orderLineId));

    const selectedProductionScheduleItems = this.getSelectedProductionScheduleItems().map(
      (productionScheduleItemInPathGroupWithIdentification: ProductionScheduleItemInPathGroupWithIdentification) =>
        productionScheduleItemInPathGroupWithIdentification.productionScheduleItemInPathGroup
    );

    return selectedProductionScheduleItems.some((productionScheduleItemInPathGroup: ProductionScheduleItemInPathGroup) =>
      productionScheduleItemsFromSelectedOrderLines.includes(productionScheduleItemInPathGroup)
    );
  }

  public canDeleteSelectedOrderLinesOrProductionScheduleItems(): boolean {
    const hasSelectedProductionScheduleItems = this.getSelectedProductionScheduleItems().length > 0;

    const selectedOrderlines = this.getSelectedOrderLines().map((productionScheduleOrderLineWithQuantity: ProductionScheduleOrderLineWithQuantity) => productionScheduleOrderLineWithQuantity.id);
    const orderLineIds = this.orderLinesHelper
      .getProductionScheduleItemsInPathGroup(this.productionSchedule)
      .map((productionScheduleItemInPathGroup: ProductionScheduleItemInPathGroup) => productionScheduleItemInPathGroup.orderLineId);

    if (hasSelectedProductionScheduleItems) {
      return selectedOrderlines.every((selectedOrderLineId: number) => orderLineIds.includes(selectedOrderLineId));
    } else {
      return selectedOrderlines.length > 0 && selectedOrderlines.every((selectedOrderLineId: number) => orderLineIds.includes(selectedOrderLineId));
    }
  }

  public updateGrid(gridModel: GridModel): void {
    this.orderLinesNewViewGrid.api.setFilterModel(gridModel.filterModel);
  }

  public deleteSelectedOrderLinesOrProductionScheduleItems(): void {
    const selectedOrderlines = this.getSelectedOrderLines().map((productionScheduleOrderLineWithQuantity: ProductionScheduleOrderLineWithQuantity) => productionScheduleOrderLineWithQuantity.id);

    const productionScheduleItemsFromSelectedOrderLines = this.orderLinesHelper
      .getProductionScheduleItemsInPathGroup(this.productionSchedule)
      .filter((productionScheduleItemInPathGroup: ProductionScheduleItemInPathGroup) => selectedOrderlines.includes(productionScheduleItemInPathGroup.orderLineId));

    const selectedProductionScheduleItems = this.getSelectedProductionScheduleItems().map(
      (productionScheduleItemInPathGroupWithIdentification: ProductionScheduleItemInPathGroupWithIdentification) =>
        productionScheduleItemInPathGroupWithIdentification.productionScheduleItemInPathGroup
    );

    const productionScheduleItemsToRemove = [...productionScheduleItemsFromSelectedOrderLines, ...selectedProductionScheduleItems];

    if (productionScheduleItemsToRemove.length > 0) {
      this.productionSchedulePlan.removeProductionScheduleItems(productionScheduleItemsToRemove);
    }
  }

  public productionSchedulePlanHasUnsavedChanges(): boolean {
    return this.productionSchedulePlan.hasUnsavedChanges(true);
  }

  private subscribeToProductionScheduleChanges(): void {
    const currentUpdatedProductionSchedule = this.syncWithProductionScheduleSubject.pipe(
      switchMap(() => this.productionSchedulePlan.currentUpdatedProductionSchedule.pipe(startWith(this.productionSchedule))),
      takeUntil(this.unSubscribeOnViewDestroy)
    );

    currentUpdatedProductionSchedule
      .pipe(
        map((productionSchedule: ProductionSchedule) =>
          productionSchedule.orderLines.map((productionScheduleOrderLine: ProductionScheduleOrderLine) =>
            ProductionScheduleOrderLineWithQuantity.fromProductionScheduleOrderLine(productionScheduleOrderLine)
          )
        )
      )
      .subscribe((productionScheduleOrderLinesWithQuantity: ProductionScheduleOrderLineWithQuantity[]) =>
        this.orderLinesNewViewGrid.api?.forEachNode((rowNode: RowNode) => {
          const orderLine = productionScheduleOrderLinesWithQuantity.find(
            (productionScheduleOrderLine: ProductionScheduleOrderLineWithQuantity) =>
              rowNode.data instanceof ProductionScheduleOrderLineWithQuantity && productionScheduleOrderLine?.id === rowNode.data.id
          );

          if (orderLine) {
            rowNode.setData(orderLine);
          }
        })
      );

    currentUpdatedProductionSchedule
      .pipe(
        map((productionSchedule: ProductionSchedule) =>
          this.orderLinesHelper
            .getProductionScheduleItemsFromProductionSchedule(productionSchedule)
            .map((productionScheduleItemInPathGroup: ProductionScheduleItemInPathGroup) => productionScheduleItemInPathGroup.orderLineId)
        )
      )
      .subscribe((orderLineIds: number[]) =>
        this.orderLinesNewViewGrid.api?.forEachNode((rowNode: RowNode<RowData>) => {
          if (rowNode.data instanceof ProductionScheduleOrderLineWithQuantity) {
            const productionScheduleOrderLineWithQuantity: ProductionScheduleOrderLineWithQuantity = rowNode.data;
            const hasOrderLine = !!orderLineIds.find((orderLineId: number) => orderLineId === productionScheduleOrderLineWithQuantity.id);

            if (hasOrderLine) {
              rowNode.key = `${productionScheduleOrderLineWithQuantity.id}`;
            }
            rowNode.setGroup(hasOrderLine);

            if (rowNode.key) {
              this.orderLinesNewViewGrid.api.refreshServerSide({
                route: [rowNode.key]
              });
            }
          }
        })
      );
  }

  public getTooltip(key: string): string {
    const translation = this.translate.instant(key);
    return translation.length > 20 ? translation : '';
  }

  private filterViewModes(currentSubscription: Subscription): void {
    if (!currentSubscription.hasPermission(Permission.TEXFAB_ORDERBOOK)) {
      this.viewModes = this.viewModes.filter(
        (viewMode: ProductionScheduleDrawingViewMode) => viewMode !== ProductionScheduleDrawingViewMode.ORDER_LINES && viewMode !== ProductionScheduleDrawingViewMode.GROUPED_ORDER_LINES
      );
    }
  }

  private getSelectedOrderLines(): ProductionScheduleOrderLineWithQuantity[] {
    return this.getSelectedNodes().filter(
      (orderLineOrItemInPathGroup: RowData) => orderLineOrItemInPathGroup instanceof ProductionScheduleOrderLineWithQuantity
    ) as ProductionScheduleOrderLineWithQuantity[];
  }

  private getSelectedProductionScheduleItems(): ProductionScheduleItemInPathGroupWithIdentification[] {
    return this.getSelectedNodes().filter(
      (orderLineOrItemInPathGroup: RowData) => orderLineOrItemInPathGroup instanceof ProductionScheduleItemInPathGroupWithIdentification
    ) as ProductionScheduleItemInPathGroupWithIdentification[];
  }

  private getSelectedNodes(): RowData[] {
    const rowNodes = this.orderLinesNewViewGrid?.api?.getSelectedNodes() ?? [];
    const existingNodes = rowNodes.filter((rowNode: RowNode) => !AssertionUtils.isNullOrUndefined(rowNode.rowIndex));
    return existingNodes.map((rowNode: RowNode) => rowNode.data) as RowData[];
  }

  private initialiseGridOptions(): void {
    this.orderLinesGridService
      .getAdditionalInfoColumnDefs()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((additionalInfoColDefs: ColDef[]) => {
        const defaultGridOptionsForOrderLines = this.gridOptionsBuilderFactoryService
          .getBuilder(this.getColumnDefinitions(additionalInfoColDefs), this.isPreSelection ? GridIdentifier.ORDER_ITEMS_NEW_PRESELECTION : GridIdentifier.ORDER_ITEMS_NEW_BUILDER)
          .withHeaderHeight(32)
          .withFloatingFiltersHeight(32)
          .withLoadingCellRenderer('customLoadingCellRenderer')
          .withComponents({
            customLoadingCellRenderer: LoadingCellOverlayComponent
          })
          .withNoRowsOverlay(
            this.isPreSelection ? this.orderLinesHelper.getNoRowsOverlayForPreselection(this.orderLinesNewViewGrid) : this.orderLinesHelper.getNoRowsOverlayForBuilder(this.noOverlayAction)
          )
          .withOnGridReady(() => {
            this.getAllPreFilterData();
          })
          .withOnFirstDataRendered(() => {
            {
              this.orderLinesNewViewGrid.api.redrawRows();
              this.syncWithProductionScheduleSubject.next();
            }
          })
          .withStopEditingWhenCellsLoseFocus()
          .withServerSideDataSource(this.SLICE_SIZE, this.getServerSideDataSource())
          .withGetRowId((params: GetRowIdParams): string => `level${params.level}id${params.data.id ?? params.data.productionScheduleItemInPathGroup.uuid}`)
          .withCompactView()
          .withGroupEdit()
          .withSingleClickEdit();

        if (this.isPreSelection) {
          this.gridOptions = defaultGridOptionsForOrderLines
            .withRowSelection(true, false)
            .withOnRowSelected((event: RowSelectedEvent) => {
              this.nodeSelected(event);
            })
            .build();
        } else {
          this.initializeGridOptionsForBuilder(defaultGridOptionsForOrderLines);
        }
      });
    this.orderLinesGridService
      .getAutoFillButtonClicked()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((isAutoFillingPath: boolean) => {
        if (isAutoFillingPath) {
          // Start auto filling with the first orderline in the list of orderlines
          this.autoFillPath(0, true);
        }
      });
  }

  private initializeGridOptionsForBuilder(defaultGridOptionsForOrderLines: GridOptionsBuilder): void {
    this.gridOptions = defaultGridOptionsForOrderLines
      .withOnCellValueChanged((event: CellValueChangedEvent) => {
        if (event.colDef.headerName !== this.translate.instant(this.quantityTranslation) && event.data.quantity > 0 && event.colDef.colId !== this.rollLengthColumnId) {
          this.addSelectedOrderLinesToPath([event.data], false, undefined);
        }
      })
      .withOnRowDoubleClicked((event: RowDoubleClickedEvent) => {
        this.processRowSelection = false;
        const colDef = this.getColumnDefinitionForClickedCell();
        if (colDef.headerName !== this.translate.instant(this.quantityTranslation) && event.data.quantity > 0 && colDef.colId !== this.rollLengthColumnId) {
          this.addSelectedOrderLinesToPath([event.data], false, undefined);
        }
        setTimeout(() => {
          this.processRowSelection = true;
        }, 1000);
      })
      .withOnRowSelected((event: RowSelectedEvent) => {
        setTimeout(() => {
          if (this.processRowSelection && this.hasQuantitySuggestionPermission) {
            this.orderLinesHelper.setQuantitySuggestionForPath(event, this.productionSchedule, this.selectedPath, this.availableSpaceInBuggyInPicks, this.buggyLengthMaxInPicks, this.fullQualities);
          }
        }, 500);
      })
      .withRowDraggingOutsideGrid(
        (params: {rowNode: RowNode; dragEvent: DragEvent}) =>
          this.orderLinesHelper.onRowDrag(params, this.productionSchedule, this.draggingService, this.renderer, this.pathWidths, this.marginInDentsOfMissingDesigns),
        (params: DndSourceCallbackParams<ProductionScheduleOrderLine | ProductionScheduleItemInPathGroupWithIdentification>) => params.node.level === 0,
        1
      )
      .withOnCellEditingStopped((event: CellEditingStoppedEvent) => {
        if (this.hasQuantitySuggestionPermission) {
          this.orderLinesHelper.recalculateQuantitySuggestionWhenRollLengthIsChanged(
            event,
            this.rollLengthColumnId,
            this.productionSchedule,
            this.selectedPath,
            this.buggyLengthMaxInPicks,
            this.availableSpaceInBuggyInPicks,
            this.fullQualities
          );
        }
      })
      .build();
  }

  private getServerSideDataSource(): IServerSideDatasource {
    if (!this.readyToFetchRows) {
      return {
        getRows: (params: IServerSideGetRowsParams): void => {
          params.success({
            rowData: [],
            rowCount: 0
          });
        }
      };
    } else {
      return {
        getRows: (params: IServerSideGetRowsParams): void => {
          const sortModel: SortModelItem[] = params.request.sortModel;
          const startRow: number = params.request.startRow;
          const endRow: number = params.request.endRow;
          let filterModel: FilterModel = params.request.filterModel;

          this.gridModel = new GridModel(startRow, endRow, sortModel, filterModel);
          this.orderLinesHelper.savedFilterModel = filterModel;

          this.orderbook
            .getSlice(OrderLinesTarget.PRODUCTION_SCHEDULE, this.gridModel, this.productionSchedule?.id?.toString())
            .pipe(
              map((orderLines: OverviewListOrderLine[]) =>
                orderLines.map((overviewListOrderLine: OverviewListOrderLine) =>
                  ProductionScheduleOrderLineWithQuantity.fromProductionScheduleOrderLine(ProductionScheduleOrderLine.fromOverviewListOrderLine(overviewListOrderLine))
                )
              ),
              takeUntil(this.unSubscribeOnViewDestroy)
            )
            .subscribe({
              next: (orderLines: ProductionScheduleOrderLineWithQuantity[]) => {
                this.orderLinesNewViewGrid.api.hideOverlay();

                let rowCount: number;
                if (params.request.groupKeys.length > 0) {
                  const productionScheduleInPathGroupWithIdentification = this.orderLinesHelper.getProductionScheduleItemsInPathGroupWithIdentification(
                    Number.parseInt(params.request.groupKeys[0], 10),
                    this.productionSchedule
                  );
                  params.success({
                    rowData: productionScheduleInPathGroupWithIdentification,
                    rowCount: productionScheduleInPathGroupWithIdentification.length
                  });
                } else {
                  if (orderLines.length < this.SLICE_SIZE) {
                    rowCount = params.request.startRow + orderLines.length;
                  }

                  if (startRow === 0) {
                    this.getStatusBarData(this.gridModel);

                    if (orderLines.length === 0) {
                      params.api.showNoRowsOverlay();
                    } else {
                      params.api.hideOverlay();
                    }
                  }

                  this.loadingForTheFirstTime = false;
                  this.gridOptions.loadingCellRendererParams = {
                    loadingForTheFirstTime: this.loadingForTheFirstTime
                  };

                  this.orderLines = orderLines;
                  params.success({
                    rowData: this.orderLines,
                    rowCount
                  });

                  this.setSelectedOrderlines(this.orderLinesNewViewGrid.api);
                }

                if (filterModel[0]) {
                  this.syncWithProductionScheduleSubject.next();
                }
              },
              error: (error: any) => this.orderLinesHelper.onServerSideDataSourceError(params, error)
            });
        }
      };
    }
  }

  private getAllPreFilterData(): void {
    let qualityObservable: Observable<WeaveQuality> = of(null);
    let coloredYarnSetObservables: Observable<ColoredYarnSet>[] = [];

    if (!AssertionUtils.isNullOrUndefined(this.addProductionScheduleNavigationState?.machineQualityId) && !AssertionUtils.isNullOrUndefined(this.addProductionScheduleNavigationState?.creelId)) {
      qualityObservable = this.getQualityById(this.addProductionScheduleNavigationState.machineQualityId);
      this.creelsService.getById(this.addProductionScheduleNavigationState.creelId).subscribe((overviewListCreel: OverviewListCreel) => {
        coloredYarnSetObservables = overviewListCreel.coloredYarnSetsWithStartDent.map((coloredYarnSetsWithStartDent: OverviewListColoredYarnSetWithStartDent) => {
          return this.getColoredYarnSetById(coloredYarnSetsWithStartDent.coloredYarnSet.id);
        });

        this.processObservables(qualityObservable, coloredYarnSetObservables);
      });
    } else if (!AssertionUtils.isNullOrUndefined(this.productionSchedule?.machineQuality)) {
      qualityObservable = this.getQualityById(this.productionSchedule.machineQuality.id);

      coloredYarnSetObservables = this.productionSchedule.coloredYarnSets.map((coloredYarnSet: OverviewListColoredYarnSetWithStartDent) => {
        return this.getColoredYarnSetById(coloredYarnSet.coloredYarnSet.id);
      });

      this.processObservables(qualityObservable, coloredYarnSetObservables);
    } else {
      this.preFilterDataFetched = true;
      this.setInitializationFilters();
    }
  }

  private processObservables(qualityObservable: Observable<WeaveQuality>, coloredYarnSetObservables: Observable<ColoredYarnSet>[]): void {
    forkJoin([qualityObservable, ...coloredYarnSetObservables])
      .pipe(
        mergeMap(([quality, ...coloredYarnSets]: [CarpetWeaveQuality, ColoredYarnSet]) => {
          this.qualityNames.push(quality?.technicalName);
          this.fullQualities.push(quality);
          this.alternativeQualityNames.push(quality?.technicalName);

          coloredYarnSets.forEach((coloredYarnSet: ColoredYarnSet) => {
            this.colorSetNames.push(coloredYarnSet?.colorSet?.name);
            this.alternativeColorSetNames.push(coloredYarnSet?.colorSet?.name);
            this.creelColorSetIds.push(coloredYarnSet?.colorSet?.id);
          });

          let alternativeQualityObservables = [];
          let alternativeColorSetObservables = [];

          if (quality.alternativeMachineQualityIds?.length > 0) {
            alternativeQualityObservables = quality?.alternativeMachineQualityIds?.map((alternativeQualityId: number) => {
              return this.getQualityById(alternativeQualityId);
            });
          }

          coloredYarnSets?.map((coloredYarnSet: ColoredYarnSet) => {
            if (coloredYarnSet.colorSet?.alternativeColorSetIds?.length > 0) {
              alternativeColorSetObservables = coloredYarnSet.colorSet.alternativeColorSetIds.map((alternativeColorSetId: number) => {
                return this.getColorSetById(alternativeColorSetId);
              });
            }
          });

          if (AssertionUtils.isEmpty(alternativeQualityObservables) && AssertionUtils.isEmpty(alternativeColorSetObservables)) {
            return of([]);
          }

          return forkJoin(alternativeQualityObservables.concat(alternativeColorSetObservables) as any);
        })
      )
      .subscribe((alternatives: (CarpetWeaveQuality | ColorSet)[]) => {
        alternatives.forEach((alternative: CarpetWeaveQuality | ColorSet) => {
          if (alternative instanceof CarpetWeaveQuality) {
            this.alternativeQualityNames.push(alternative.technicalName);
            this.fullQualities.push(alternative);
          } else if (alternative instanceof ColorSet) {
            this.alternativeColorSetNames.push(alternative.name);
          }
        });

        this.preFilterDataFetched = true;
        this.setInitializationFilters();
      });
  }

  private getQualityById(id: number): Observable<WeaveQuality> {
    return this.qualities.getById(id);
  }

  private getColoredYarnSetById(id: number): Observable<ColoredYarnSet> {
    return this.coloredYarnSets.getById(id);
  }

  private getColorSetById(id: number): Observable<ColorSet> {
    return this.colorSets.getById(id);
  }

  private nodeSelected(event: RowSelectedEvent): void {
    const selectedRows = event.api.getSelectedNodes();
    const selectedOrderlines = selectedRows.map((rowNode: RowNode<OverviewListOrderLine>) => rowNode.data);
    const selectedOrderlineIds = selectedOrderlines.map((orderline: ProductionScheduleOrderLine) => orderline.id);

    const selectedOrderlinesWithoutDuplicates = [...new Set([...selectedOrderlineIds, ...this.preselectedOrderlineIds])];
    let finalOrderlinesIdList = selectedOrderlinesWithoutDuplicates;

    if (event.node.isSelected() === false) {
      finalOrderlinesIdList = selectedOrderlinesWithoutDuplicates.filter((orderlineId: number) => orderlineId !== event.data.id);
    }

    this.preselectedOrderlineIds = finalOrderlinesIdList;

    if (this.isPreSelection) {
      this.orderlineSelected.emit(this.preselectedOrderlineIds);
    }
  }

  private setInitializationFilters(): void {
    if (!this.preFilterDataFetched) {
      return;
    }

    let filterModel: any;

    filterModel = this.orderLinesHelper.getInitializationFilterModel(
      this.showPreselectedItems,
      this.productionSchedule,
      this.selectedPath,
      this.selectedRestPathColoredYarnSet,
      this.preselectedOrderlineIds,
      this.addProductionScheduleNavigationState,
      this.productionOrderCustomSettings,
      this.alternativeQualityNames,
      this.qualityNames,
      this.alternativeColorSetNames,
      this.colorSetNames,
      this.showOnlyQueryCompatibleOrderlines,
      this.isPreSelection,
      this.selectedPathContentRowData
    );

    this.readyToFetchRows = true;
    this.orderLinesNewViewGrid?.api?.setFilterModel(filterModel);

    //because we fetched no rows yet, we need to explictly trigger a new getrows on aggrid
    this.orderLinesNewViewGrid?.api?.setGridOption('serverSideDatasource', this.getServerSideDataSource());
  }

  private getStatusBarData(gridModel: GridModel): void {
    this.statusBarService
      .getStatusBarData(StatusBarTarget.ORDER_LINES, gridModel)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((statusBar: StatusBar) => {
        this.statusBar = statusBar;
      });
  }

  private autoFillPath(autoFillIndex: number, startingAutoFill: boolean = false): void {
    this.orderLinesGridService.setIsFillingPath(true);

    if (startingAutoFill) {
      const longestPathLength = this.orderLinesHelper.getLongestPathLengthInPicks(this.productionSchedule);

      this.availableSpaceForAutoFill = longestPathLength !== 0 ? longestPathLength : (this.availableSpaceInBuggyInPicks ?? this.buggyLengthMaxInPicks);

      this.orderlinesThatAreUsedToAutoFill = this.orderLines;

      if (!AssertionUtils.isNullOrUndefined(this.selectedPath) && AssertionUtils.isNullOrUndefined(this.availableSpaceInBuggyInPicks)) {
        this.availableSpaceForAutoFill -= this.selectedPath.totalLengthInPicks;
      }
    }

    if (AssertionUtils.isNullOrUndefined(this.orderlinesThatAreUsedToAutoFill) || AssertionUtils.isEmpty(this.orderlinesThatAreUsedToAutoFill)) {
      this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
        titleText: 'PRODUCTION_ORDER.WEAVE_PLAN.PATH_LAYOUT.AUTO_FILL_WARNING',
        messageText: 'PRODUCTION_ORDER.WEAVE_PLAN.PATH_LAYOUT.NO_ORDERLINES_ITEMS',
        type: DialogType.INFORMATION
      });
    } else if (!AssertionUtils.isNullOrUndefined(this.orderlinesThatAreUsedToAutoFill) && !AssertionUtils.isNullOrUndefined(this.orderlinesThatAreUsedToAutoFill[autoFillIndex])) {
      this.addSelectedOrderLinesToPath([this.orderlinesThatAreUsedToAutoFill[autoFillIndex]], true, autoFillIndex);
    }
  }

  private addSelectedOrderLinesToPath(selectedOrderLines: ProductionScheduleOrderLineWithQuantity[], isAutoFilling: boolean, autoFillIndex: number): void {
    this.orderLinesNewViewGrid?.api?.getSelectedNodes()?.forEach((rowNode: RowNode) => rowNode?.setSelected(false));

    const listDrawingOrderLines = selectedOrderLines.map((productionScheduleOrderLine: ProductionScheduleOrderLineWithQuantity) =>
      this.orderLinesHelper.createListDrawingOrderLine(
        this.productionSchedule,
        productionScheduleOrderLine,
        this.productionSchedule.machineQuality.weaveStructure.picksPerColorLine,
        this.marginInDentsOfMissingDesigns,
        this.pathWidths,
        isAutoFilling,
        this.selectedPath,
        this.availableSpaceForAutoFill,
        this.buggyLengthMaxInPicks,
        this.fullQualities
      )
    );

    listDrawingOrderLines.forEach((listDrawingOrderLine: ListDrawingOrderLine) => {
      let overviewListColoredYarnSetWithStartDent: OverviewListColoredYarnSetWithStartDent;

      overviewListColoredYarnSetWithStartDent = this.productionSchedule.coloredYarnSets.find(
        (cys: OverviewListColoredYarnSetWithStartDent) => cys.startDent === this.productionSchedulePathsOfColoredYarnSet?.productionScheduleColoredYarnSet?.startDent
      );

      if (
        !AssertionUtils.isNullOrUndefined(this.selectedPath) &&
        this.productionSchedule.canAddProductionScheduleItemToProductionSchedulePathForNewBuilder(listDrawingOrderLine, this.selectedPath.technicalWidthInDents) &&
        listDrawingOrderLine.amount > 0
      ) {
        if (isAutoFilling) {
          const totalHeightInPicks = this.orderLinesHelper.getTotalCutbarLengthInPicks(selectedOrderLines[0], this.fullQualities) + selectedOrderLines[0].article.technicalHeightInPicks;
          const amountToDeduct = totalHeightInPicks * (selectedOrderLines[0].quantity / this.productionScheduleItemMultiplier);
          if (!isNaN(amountToDeduct) && !AssertionUtils.isNullOrUndefined(amountToDeduct)) {
            this.availableSpaceForAutoFill -= amountToDeduct;
          }
        }
        this.addSelectedOrderLinesToSelectedPath(listDrawingOrderLine, overviewListColoredYarnSetWithStartDent, isAutoFilling, autoFillIndex);
      } else if (
        AssertionUtils.isNullOrUndefined(this.selectedPath) &&
        this.productionSchedule.canAddProductionScheduleItemToColoredYarnSet(overviewListColoredYarnSetWithStartDent.startDent, listDrawingOrderLine) &&
        listDrawingOrderLine.amount > 0
      ) {
        if (isAutoFilling) {
          const totalHeightInPicks = this.orderLinesHelper.getTotalCutbarLengthInPicks(selectedOrderLines[0], this.fullQualities) + selectedOrderLines[0].article.technicalHeightInPicks;
          const amountToDeduct = totalHeightInPicks * (selectedOrderLines[0].quantity / this.productionScheduleItemMultiplier);
          if (!isNaN(amountToDeduct) && !AssertionUtils.isNullOrUndefined(amountToDeduct)) {
            this.availableSpaceForAutoFill -= amountToDeduct;
          }
        }
        this.addSelectedOrderLinesToNewPath(listDrawingOrderLine, isAutoFilling);
      } else if (!isAutoFilling) {
        this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
          titleText: 'PRODUCTION_ORDER.WEAVE_PLAN.WEAVE_PLAN',
          messageText: 'PRODUCTION_ORDER.WEAVE_PLAN.WARNINGS.PATH_WIDTH_DOES_NOT_EQUAL_DESIGN_WIDTH',
          type: DialogType.INFORMATION
        });
      } else {
        this.autoFillPath(autoFillIndex + 1);
      }
    });

    if (!AssertionUtils.isNullOrUndefined(this.selectedPath) && isAutoFilling) {
      this.selectNextPathIfPossible(this.selectedPath);
    }
  }

  private selectNextPathIfPossible(selectedPath: ProductionSchedulePath): void {
    this.productionSchedulePathsOfColoredYarnSet?.productionSchedulePaths.forEach((path: ProductionSchedulePath, index: number) => {
      if (path.pathNumber === selectedPath.pathNumber + 1) {
        this.selectedPath = path;
        this.selectedPathOutput.emit(this.selectedPath);
      }
    });

    if (this.selectedPath === selectedPath) {
      this.selectedRestPathColoredYarnSet = this.productionSchedule?.productionSchedulePathsOfColoredYarnSets[0]?.productionScheduleColoredYarnSet;
      this.selectedPath = undefined;
      this.selectedPathOutput.emit(this.selectedPath);
      this.selectedRestPathColoredYarnSetOutput.emit(this.selectedRestPathColoredYarnSet);
    }
  }

  private getColumnDefinitionForClickedCell(): ColDef {
    const column = this.orderLinesNewViewGrid.api.getAllDisplayedColumns().find((col: Column) => col.getColId() === this.orderLinesNewViewGrid.api.getFocusedCell().column.getColId());
    return column ? column.getColDef() : null;
  }

  private getColumnDefinitions(additionalInfoColDefs: ColDef[]): ColDef[] {
    return [
      this.colDefBuilderFactoryService.getBuilder().withColIdAndField('id').withNumericMultiFilter(this.getPossibleColumnsValues('id')).withHide().withHiddenInToolPanel().build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('add')
        .withHeaderName('PRODUCTION_ORDER.DESIGN_LIBRARY.ORDER_LINES.ADD_TO_PATH')
        .withCellRenderer(ProgressCellRendererComponent, {
          label: 'GENERAL.ACTIONS.ADD',
          isOrderline: true
        })
        .withoutFilter()
        .withSuppressMovable()
        .withMinWidth(112)
        .withMaxWidth(150)
        .withHide(this.isPreSelection)
        .withHiddenInToolPanel()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('quantity')
        .withHeaderName(this.quantityTranslation)
        .withRightAlignment()
        .withSortable(false)
        .withEditable((params: EditableCallbackParams<RowData>) => this.orderLinesHelper.isQuantityEditable(params, this.productionSchedule, this.productionOrderCustomSettings))
        .withoutFilter()
        .withOnCellClicked((event: CellClickedEvent) => this.editQuantityCell(event))
        .withCellEditor(
          NumberCellEditorComponent,
          (params: ICellEditorParams<RowData>): NumberCellEditorParams =>
            ({
              min:
                params.node.level === 0 && params.data instanceof ProductionScheduleOrderLineWithQuantity && params.data.article.design.repeated
                  ? 1
                  : this.productionSchedule.getProductionScheduleItemMultiplier(),
              max:
                !this.productionOrderCustomSettings?.allowExceedingRequestedAmountOfOrderLineItems && params.node.level === 0 && params.data instanceof ProductionScheduleOrderLineWithQuantity
                  ? this.orderLinesHelper.getMaxQuantityToPlan(params.data, this.productionSchedule)
                  : undefined,
              autoSelect: true
            }) as NumberCellEditorParams
        )
        .withValueGetter((params: ValueGetterParams<RowData>) => {
          if (params.data instanceof ProductionScheduleOrderLineWithQuantity && params.data?.quantity) {
            return params.data.quantity;
          } else if (params.data instanceof ProductionScheduleOrderLineWithQuantity) {
            return this.orderLinesHelper.getQuantitySuggestionForPath(
              params.data,
              this.productionSchedule,
              this.selectedPath,
              this.availableSpaceInBuggyInPicks,
              this.buggyLengthMaxInPicks,
              this.fullQualities,
              this.hasQuantitySuggestionPermission,
              this.productionScheduleItemMultiplier
            );
          }
        })
        .withValueSetter((params: ValueSetterParams<ProductionScheduleOrderLineWithQuantity>) => this.setQuantity(params))
        .withSuppressMovable()
        .withHide(this.isPreSelection)
        .withWidth(100)
        .withMinWidth(100)
        .withMaxWidth(150)
        .withHiddenInToolPanel()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId(this.rollLengthColumnId)
        .withField(`rollLengthInMeter`)
        .withHeaderName('PRODUCTION_ORDER.WEAVE_PLAN.PATH_LAYOUT.ROLL_LENGTH', undefined, false, Unit.METER)
        .withOnCellClicked((event: CellClickedEvent) => this.editQuantityCell(event))
        .withSortable(false)
        .withEditable((params: EditableCallbackParams<ProductionScheduleOrderLineWithQuantity>) => this.orderLinesHelper.isRollLengthEditable(params))
        .withCellEditor(NumberCellEditorComponent, (): NumberCellEditorParams => ({isSmall: false, autoSelect: true}) as NumberCellEditorParams)
        .withValueGetter((params: ValueGetterParams<ProductionScheduleOrderLineWithQuantity>) => {
          if (params.data?.rollLengthInMeter && params.data?.article?.commercialHeightInMM / 1000 < params.data.rollLengthInMeter) {
            return params.data.rollLengthInMeter;
          } else if (params.data?.article?.design?.repeated) {
            const rollLengthInMeter = params.data?.article?.commercialHeightInMM / 1000;
            if (!AssertionUtils.isNullOrUndefined(rollLengthInMeter)) {
              params.data.rollLengthInMeter = rollLengthInMeter;
              return params.data.rollLengthInMeter;
            } else {
              return 1;
            }
          } else {
            return null;
          }
        })
        .withMinWidth(130)
        .withHide(this.isPreSelection)
        .withHiddenInToolPanel(this.isPreSelection)
        .withoutFilter()
        .withRightAlignment()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('amountLeft')
        .withValueGetter((params: ValueGetterParams<RowData>): number => this.orderLinesHelper.getAmountLeft(params.data, this.productionSchedule))
        .withHeaderName('ORDERBOOK.TO_PLAN_AMOUNT')
        .withSortable(false)
        .withRightAlignment()
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withNumericMultiFilter(this.getPossibleColumnsValues('amountLeft'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('totalCount')
        .withValueGetter((params: ValueGetterParams<RowData>): number => this.orderLinesHelper.getTotalCount(params))
        .withHeaderName('ORDERBOOK.REQUESTED_AMOUNT')
        .withSortable(false)
        .withRightAlignment()
        .withNumericMultiFilter(this.getPossibleColumnsValues('totalCount'))
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('salesOrderReference', true)
        .withHeaderName('ORDERBOOK.ORDERBOOK_REFERENCE')
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withTextMultiFilter(this.getPossibleColumnsValues('salesOrderReference'))
        .withLockVisible()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('salesOrderLineReference', true)
        .withHeaderName('ORDERBOOK.ORDERLINE_REFERENCE')
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withTextMultiFilter(this.getPossibleColumnsValues('salesOrderLineReference'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('article')
        .withField('article.name', true)
        .withHeaderName('ARTICLES.ARTICLE')
        .withComparator(StringUtils.stringComparator)
        .withTextMultiFilter(this.getPossibleColumnsValues('article'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('designCode')
        .withField('article.design.code', true)
        .withHeaderName('DESIGN_LIBRARY.DETAILS.DESIGN_CODE')
        .withComparator(StringUtils.stringComparator)
        .withTextMultiFilter(this.getPossibleColumnsValues('designCode'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('commercialWidthInCM')
        .withField('article.commercialWidthInMM')
        .withHeaderName('GENERAL.DIMENSIONS.WIDTH', 1, false, Unit.CENTIMETER)
        .withCellRenderer((params: ICellRendererParams) => AgGridUtils.buildAgGridCellTextWithoutUnit(params.data.article.commercialWidthInMM, Unit.MILLIMETER, Unit.CENTIMETER, this.l10nIntlService))
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnitConversion(params.value, Unit.MILLIMETER, this.commercialUnit, this.l10nIntlService))
        .withRightAlignment()
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withNumericMultiFilter(this.getPossibleColumnsValues('commercialWidthInCM'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('commercialHeightInCM')
        .withField('article.commercialHeightInMM')
        .withHeaderName('GENERAL.DIMENSIONS.HEIGHT', 1, false, Unit.CENTIMETER)
        .withCellRenderer((params: ICellRendererParams) => AgGridUtils.buildAgGridCellTextWithoutUnit(params.data.article.commercialHeightInMM, Unit.MILLIMETER, Unit.CENTIMETER, this.l10nIntlService))
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnitConversion(params.value, Unit.MILLIMETER, this.commercialUnit, this.l10nIntlService))
        .withRightAlignment()
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withNumericMultiFilter(this.getPossibleColumnsValues('commercialHeightInCM'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('technicalWidthInDents')
        .withField('article.technicalWidthInDents')
        .withHeaderName('GENERAL.DIMENSIONS.WIDTH', 1, false, Unit.DENTS)
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnit(params.value, Unit.DENTS, this.l10nIntlService))
        .withRightAlignment()
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withNumericMultiFilter(this.getPossibleColumnsValues('technicalWidthInDents'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('technicalHeightInPicks')
        .withField('article.technicalHeightInPicks')
        .withHeaderName('GENERAL.DIMENSIONS.HEIGHT', 1, false, Unit.PICKS)
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnit(params.value, Unit.PICKS, this.l10nIntlService))
        .withRightAlignment()
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withNumericMultiFilter(this.getPossibleColumnsValues('technicalHeightInPicks'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('amountInProductionSchedule')
        .withValueGetter((params: ValueGetterParams<RowData>): number => this.orderLinesHelper.getAmountInProductionSchedule(params.data, this.productionSchedule))
        .withHeaderName('ORDERBOOK.AMOUNT_IN_PRODUCTION_SCHEDULE')
        .withHide(this.isPreSelection)
        .withHiddenInToolPanel(this.isPreSelection)
        .withRightAlignment()
        .withSortable(false)
        .withNumericMultiFilter(this.getPossibleColumnsValues('amountInProductionSchedule'))
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('machineQualityName')
        .withField('article.machineQuality.nameWithVersion')
        .withHeaderName('TEXTILE_DATA.MACHINE_QUALITY.MACHINE_QUALITY')
        .withTooltipField('article.machineQuality.name')
        .withComparator(StringUtils.stringComparator)
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withTextMultiFilter(this.getPossibleColumnsValues('machineQualityName'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('colorSetName')
        .withField('article.colorSet.name')
        .withHeaderName('TEXTILE_DATA.COLOR_SET.COLOR_SET')
        .withComparator(StringUtils.stringComparator)
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withTextMultiFilter(this.getPossibleColumnsValues('colorSetName'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('mainColor')
        .withField('article.mainColor.name', true)
        .withHeaderName('GENERAL.ADVANCED_SEARCH.MAIN_COLOR')
        .withComparator(StringUtils.stringComparator)
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withTextMultiFilter(this.getPossibleColumnsValues('mainColor'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('borderColor')
        .withField('article.borderColor.name', true)
        .withHeaderName('GENERAL.ADVANCED_SEARCH.BORDER_COLOR')
        .withComparator(StringUtils.stringComparator)
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withTextMultiFilter(this.getPossibleColumnsValues('borderColor'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('shape')
        .withField('article.design.shape', true)
        .withHeaderName('GENERAL.SHAPE')
        .withComparator(StringUtils.stringComparator)
        .withValueGetter((params: ValueGetterParams) => this.getShape(params.data.article?.design?.shape), true)
        .withTextMultiFilter(this.getPossibleColumnsValues('shape'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('finishingName')
        .withField('article.finishing.nameWithVersion', true)
        .withHeaderName('TEXTILE_DATA.FINISHING.FINISHING')
        .withComparator(StringUtils.stringComparator)
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withTextMultiFilter(this.getPossibleColumnsValues('finishingName'))
        .build(),
      ...additionalInfoColDefs,
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('desiredDate')
        .withHeaderName('SALES_ORDERS.DESIRED_DATE')
        .withDate()
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withDateMultiFilter(this.getPossibleColumnsValues('desiredDate'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('customerReference', true)
        .withHeaderName('ORDERBOOK.CUSTOMER_REFERENCE')
        .withComparator(StringUtils.stringComparator)
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withTextMultiFilter(this.getPossibleColumnsValues('customerReference'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('priority')
        .withHeaderName('SALES_ORDERS.PRIORITY.PRIORITY')
        .withCellRenderer(GridTagComponent, {
          translationKey: 'SALES_ORDERS.PRIORITY.',
          size: TagSize.SMALL,
          colorGetter: {
            [Priority.LOW]: TagColor.PRIMARY,
            [Priority.MEDIUM]: TagColor.WARNING,
            [Priority.HIGH]: TagColor.ERROR
          }
        })
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withTextMultiFilter(this.getPossibleColumnsValues('priority'))
        .withTooltipValueGetter((params: ITooltipParams) => this.translate.instant(`SALES_ORDERS.PRIORITY.${params.value}`).toUpperCase())
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('status')
        .withHeaderName('GENERAL.STATUS')
        .withCellRendererSelector(
          (params: ICellRendererParams): CellRendererSelectorResult =>
            params.node.level === 0
              ? {
                  component: OrderLineStatusCellRendererComponent,
                  params: {
                    productionSchedule: this.productionSchedule
                  } as OrderLineStatusCellRendererParams
                }
              : {
                  component: ProductionScheduleItemInPathGroupStatusCellRendererComponent,
                  params: {
                    productionSchedule: this.productionSchedule
                  }
                }
        )
        .withLockVisible()
        .withTextMultiFilter(this.getPossibleColumnsValues('status'))
        .withTooltipValueGetter((params: ITooltipParams) =>
          params.node.level === 0
            ? this.translate.instant(`ORDERBOOK.ORDER_LINE_STATUS.${params.value}`).toUpperCase()
            : this.translate.instant('ORDERBOOK.IN_PATH', {pathNumber: params?.data?.productionSchedulePath?.pathNumber})
        )
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('amountProduced')
        .withHeaderName('ORDERBOOK.PRODUCED_AMOUNT')
        .withValueGetter((params: ValueGetterParams) => {
          return `${params.data.amountProduced}`;
        })
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withNumericMultiFilter(this.getPossibleColumnsValues('amountProduced'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('amountPlanned')
        .withHeaderName('ORDERBOOK.PLANNED_AMOUNT')
        .withValueGetter((params: ValueGetterParams) => {
          return `${params.data.amountPlanned}`;
        })
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withNumericMultiFilter(this.getPossibleColumnsValues('amountPlanned'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('orderType')
        .withHeaderName('ORDERBOOK.ORDER_TYPE')
        .withCellRendererSelector(this.orderLinesHelper.cellRendererSelectorForOrderLinesOnly())
        .withTextMultiFilter(this.getPossibleColumnsValues('orderType'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('designName')
        .withField('article.design.name', true)
        .withHeaderName('DESIGN_LIBRARY.DETAILS.DESIGN_NAME')
        .withComparator(StringUtils.stringComparator)
        .withTextMultiFilter(this.getPossibleColumnsValues('designName'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('missingDesign')
        .withHeaderName('ORDERBOOK.MISSING_DESIGN')
        .withValueGetter((params: ValueGetterParams) => (params.data.missingDesign ? this.translate.instant(this.booleanTrueTranslation) : this.translate.instant(this.booleanFalseTranslation)), true)
        .withBooleanFilter(this.translate.instant(this.booleanFalseTranslation), this.translate.instant(this.booleanTrueTranslation))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('compatibleColorSetId')
        .withNumericMultiFilter(this.getPossibleColumnsValues('article.colorSet.id'))
        .withHiddenInToolPanel()
        .withField('article.colorSet.id')
        .build()
    ];
  }

  private getPossibleColumnsValues(columnIdentifier: string): Observable<string[]> {
    return this.orderbook.getPossibleValues(columnIdentifier).pipe(takeUntil(this.unSubscribeOnViewDestroy));
  }

  private addSelectedOrderLinesToSelectedPath(
    listDrawingOrderLine: ListDrawingOrderLine,
    overviewListColoredYarnSetWithStartDent: OverviewListColoredYarnSetWithStartDent,
    isAutoFilling: boolean,
    autoFillIndex: number
  ): void {
    this.productionSchedulePlan
      .addDrawingToProductionSchedulePath(listDrawingOrderLine, this.selectedPath.pathNumber, overviewListColoredYarnSetWithStartDent)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((path: ProductionSchedulePath) => {
        if (!AssertionUtils.isNullOrUndefined(path)) {
          this.selectedPath = path;
          this.selectedPathOutput.emit(this.selectedPath);
          this.selectedRestPathColoredYarnSet = undefined;
          this.selectedRestPathColoredYarnSetOutput.emit(this.selectedRestPathColoredYarnSet);

          if (isAutoFilling) {
            this.autoFillPath(autoFillIndex + 1);
          }
        }
      });
  }

  private addSelectedOrderLinesToNewPath(listDrawingOrderLine: ListDrawingOrderLine, isAutoFilling: boolean): void {
    this.productionSchedulePlan
      .addDrawingToColoredYarnSet(listDrawingOrderLine, this.selectedRestPathColoredYarnSet.startDent, undefined)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((path: ProductionSchedulePath) => {
        if (!AssertionUtils.isNullOrUndefined(path)) {
          this.selectedPath = path;
          this.selectedRestPathColoredYarnSet = undefined;
          this.selectedPathOutput.emit(this.selectedPath);
          this.selectedRestPathColoredYarnSetOutput.emit(this.selectedRestPathColoredYarnSet);

          if (isAutoFilling) {
            this.autoFillPath(1);
          }
        }
      });
  }

  private setSelectedOrderlines(gridApi: GridApi): void {
    gridApi?.deselectAll();
    if (this.preselectedOrderlineIds?.length > 0) {
      gridApi?.forEachNode((node: RowNode) => {
        if (!AssertionUtils.isNullOrUndefined(node.data)) {
          node.setSelected(this.preselectedOrderlineIds.some((orderlineId: number) => orderlineId === node.data.id));
        }
      });
    }
  }

  private editQuantityCell(event: CellClickedEvent): void {
    if (this.orderLinesNewViewGrid?.api) {
      this.orderLinesNewViewGrid?.api?.stopEditing();
    }

    if (event.column.getColDef().editable) {
      this.orderLinesNewViewGrid?.api.startEditingCell({
        rowIndex: event?.rowIndex,
        colKey: event?.column?.getColId()
      });
    }
  }

  private setQuantity(params: ValueSetterParams<ProductionScheduleOrderLineWithQuantity>): boolean {
    if (this.productionScheduleItemMultiplier === 2 && params.newValue % 2 !== 0) {
      params.newValue--;
    }

    params.node.data.quantity = params.newValue;

    return true;
  }

  private getShape(shape: string): string {
    return FixedShape.getTranslationKeyForShape(shape, this.translate);
  }

  private subscribeToOverlayActions(): void {
    this.overlayActionsService.actionTriggeredEmitter.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((actionKey: string) => {
      if (actionKey === this.noOverlayAction[0].key) {
        this.navigateBackOutput.emit();
      }
    });
  }
}
